import { useEffect, useRef, useState } from 'react';

import { useOutletContext } from 'react-router-dom';

import { LANDING_SCROLL_ROOT_ID } from '@pages/LandingPage/model/scroll';

import { LandingSpheresAnimation } from './Animation';
import { LandingHero, LandingHeroText } from './Hero';
import { LandingFooter } from './LandingFooter';
import type { LandingOutletContext } from './LandingRouteFrame';
import {
  LandingProductsGrid,
  LandingProductsText,
} from './Products';
import { LandingVisionSection } from './Vision';
import {
  landingHeroDescription,
  landingHeroTitleFirstLine,
  landingHeroTitleSecondLine,
  landingNavItems,
  landingProductItems,
  landingProductsSectionHeading,
  landingProductsSectionLabel,
} from '../model/content';

const productsTitleShowThreshold = 1;

export const LandingPage = () => {
  const { onVisibleSectionChange } = useOutletContext<LandingOutletContext>();
  const [isProductsTitleVisible, setIsProductsTitleVisible] = useState(false);
  const [spheresSceneIndex, setSpheresSceneIndex] = useState(0);
  const productsTitleTriggerRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    const scrollRoot = document.getElementById(LANDING_SCROLL_ROOT_ID);
    const sectionRatios = new Map<string, number>();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            sectionRatios.set(entry.target.id, entry.intersectionRatio);
          } else {
            sectionRatios.delete(entry.target.id);
          }
        });

        const visibleId = [...sectionRatios.entries()].sort(([, a], [, b]) => b - a)[0]?.[0];
        const activeId = visibleId === 'vision' || visibleId === 'products' ? visibleId : null;

        onVisibleSectionChange(activeId);
      },
      {
        root: scrollRoot,
        rootMargin: '-20% 0px -50% 0px',
        threshold: [0, 0.15, 0.35, 0.6],
      }
    );

    landingNavItems.forEach(({ id }) => {
      const section = document.getElementById(id);

      if (section) {
        observer.observe(section);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [onVisibleSectionChange]);

  useEffect(() => {
    const trigger = productsTitleTriggerRef.current;
    const scrollRoot = document.getElementById(LANDING_SCROLL_ROOT_ID);

    if (!trigger || isProductsTitleVisible) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && entry.intersectionRatio >= productsTitleShowThreshold) {
          setIsProductsTitleVisible(true);
          observer.disconnect();
        }
      },
      {
        root: scrollRoot,
        threshold: [productsTitleShowThreshold],
      }
    );

    observer.observe(trigger);

    return () => {
      observer.disconnect();
    };
  }, [isProductsTitleVisible]);

  return (
    <div className="relative min-h-svh">
      <div className="pointer-events-none absolute inset-0 overflow-clip">
        <LandingSpheresAnimation sceneIndex={spheresSceneIndex} />
      </div>
      <div className="relative z-10 mx-auto flex w-full max-w-full flex-col">
        <main
          className="
            relative flex flex-col gap-12
            lg:gap-16
          "
        >
          <div className="relative grid h-fit w-full">
            <div className="relative z-10 col-start-1 row-start-1">
              <LandingHero>
                <LandingHeroText
                  description={landingHeroDescription}
                  headingId="landing-vision-heading"
                  firstLine={landingHeroTitleFirstLine}
                  secondLine={landingHeroTitleSecondLine}
                />
              </LandingHero>
              <LandingVisionSection onSpheresSceneIndexChange={setSpheresSceneIndex} />
            </div>
          </div>
          <section
            id="products"
            className="
              relative mx-auto -mt-32 max-w-400 scroll-mt-24
              md:scroll-mt-48
            "
            aria-labelledby="landing-products-heading"
          >
            <div className="flex flex-col gap-10">
              <div className="landing-container flex flex-col gap-4">
                <LandingProductsText
                  ref={productsTitleTriggerRef}
                  headingId="landing-products-heading"
                  label={landingProductsSectionLabel}
                  heading={landingProductsSectionHeading}
                  animate={isProductsTitleVisible}
                />
              </div>
              <LandingProductsGrid products={landingProductItems} />
            </div>
          </section>
        </main>

        <LandingFooter />
      </div>
    </div>
  );
};
